/**
 * OfferSearchSelect — reusable searchable-offer dropdown.
 *
 * Wraps the /api/offers?search=&limit= endpoint in a debounced combobox.
 * Drop this anywhere Nick needs to pick an offer — one UX everywhere,
 * zero per-call-site bugs typing an offer_id.
 *
 * Usage:
 *   <OfferSearchSelect
 *     value={offerId}
 *     onChange={(offerId, offer) => { ... }}
 *     placeholder="Select offer..."
 *     required
 *   />
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OfferSearchSelect({
  value,                 // current offer_id
  onChange,              // (offerId, offerDoc | null) => void
  placeholder = "Type to search offers — name, code, or id",
  required = false,
  disabled = false,
  limit = 20,
  className = "",
  testIdPrefix = "offer-select",
  autoFocus = false,
  // extra filters forwarded to /api/offers (e.g. business_unit_id, status)
  extraQuery = null,
}) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvedOffer, setResolvedOffer] = useState(null);
  const blurTimerRef = useRef(null);

  // When `value` changes from the outside, fetch the offer doc so we can
  // display its display name in the input.
  useEffect(() => {
    if (!value) {
      setResolvedOffer(null);
      setSearch("");
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/offers/${value}`, { withCredentials: true });
        setResolvedOffer(r.data);
        setSearch((prev) => (prev ? prev : (r.data?.name || r.data?.offer_code || value)));
      } catch {
        // leave search untouched — user may have typed something external
      }
    })();
  }, [value]);

  // Debounced server search when dropdown is open.
  useEffect(() => {
    if (!showOptions || disabled) return;
    const term = search.trim();
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit };
        if (term) params.search = term;
        Object.assign(params, extraQuery || {});
        const r = await axios.get(`${API}/offers`, { params, withCredentials: true });
        const data = Array.isArray(r.data) ? r.data : (r.data?.offers || []);
        const lower = term.toLowerCase();
        const filtered = !term
          ? data
          : data.filter((o) => {
              const hay = `${o.name || ""} ${o.offer_code || ""} ${o.offer_id || ""}`.toLowerCase();
              return hay.includes(lower);
            });
        setOptions(filtered.slice(0, limit));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [search, showOptions, limit, disabled, extraQuery]);

  const handlePick = useCallback(
    (offer) => {
      setResolvedOffer(offer);
      setSearch(offer.name || offer.offer_code || offer.offer_id);
      setShowOptions(false);
      if (onChange) onChange(offer.offer_id, offer);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setResolvedOffer(null);
    setSearch("");
    setShowOptions(false);
    if (onChange) onChange("", null);
  }, [onChange]);

  return (
    <div className={`relative ${className}`} data-testid={`${testIdPrefix}-wrapper`}>
      <input
        type="text"
        value={search}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(e) => {
          setSearch(e.target.value);
          setShowOptions(true);
          if (!e.target.value && value) {
            // Typing-to-clear
            if (onChange) onChange("", null);
            setResolvedOffer(null);
          }
        }}
        onFocus={() => setShowOptions(true)}
        onBlur={() => {
          // Delay so clicking a suggestion registers before the dropdown closes.
          blurTimerRef.current = setTimeout(() => setShowOptions(false), 150);
        }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm pr-8 text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
        data-testid={`${testIdPrefix}-input`}
      />
      {value && !disabled && (
        <button
          type="button"
          onMouseDown={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs"
          data-testid={`${testIdPrefix}-clear`}
          title="Clear selection"
        >
          ×
        </button>
      )}
      {showOptions && !disabled && (
        <div
          className="absolute z-30 mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-400 italic">Searching…</div>
          )}
          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400 italic">
              No matching offers.
            </div>
          )}
          {!loading &&
            options.map((o) => (
              <button
                key={o.offer_id}
                type="button"
                onMouseDown={() => handlePick(o)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 border-b border-slate-800 last:border-b-0 ${
                  value === o.offer_id ? "bg-slate-800" : ""
                }`}
                data-testid={`${testIdPrefix}-option-${o.offer_id}`}
              >
                <div className="text-white">{o.name || o.offer_code || o.offer_id}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {o.offer_code || ""}{o.vertical ? ` · ${o.vertical}` : ""}
                  {o.payout ? ` · $${o.payout}` : ""}
                </div>
              </button>
            ))}
        </div>
      )}
      {resolvedOffer && !showOptions && (
        <div className="mt-1 text-[11px] text-slate-500" data-testid={`${testIdPrefix}-summary`}>
          <span className="text-emerald-400">✓</span>{" "}
          {resolvedOffer.vertical && <span>{resolvedOffer.vertical} · </span>}
          <span className="font-mono">{resolvedOffer.offer_id}</span>
        </div>
      )}
    </div>
  );
}
